/**
 * The event pages are mounted twice - once under /calendar/, once inside a
 * community - so their route names carry the prefix of whichever mount they
 * belong to. With one router the two mounts share a name map, and an unprefixed
 * 'info' from a community event would navigate into the calendar.
 *
 * The wiki pages, mounted three times, are namespaced the same way.
 */
const EVENT_NAME_PREFIX_META = 'eventNamePrefix';

/**
 * An event page can be scoped to a calendar day, and to a calendar owner on top of it.
 * Those three segments used to be trailing optional params on the parent path
 * (`:date(...)?/:calendarOwnerId(\d+)?/:calendarOwnerName?/`), which router 4 reads
 * differently from router 3: an ambiguous segment goes to a trailing optional param
 * rather than to a child route, so `/calendar/event/4/info` resolved with
 * calendarOwnerName="info" and never reached the info page.
 *
 * So each shape is spelled out as a record of its own instead. With the segments of a
 * given record either all present or all absent there is nothing to be ambiguous about,
 * and the date param is narrow enough (`\d{4}-\d{2}-\d{2}`) that no child's static
 * segment can be read as one - router 4 ranks the static segment above a param anyway.
 *
 * The day-only shape is the one django itself hands out: `Event.get_absolute_url()`
 * reverses `event_detail_with_date` and returns `/calendar/event/<pk>/<date>/`, with no
 * owner, and getEventUrl() builds the same for a group's event list. EventDetailPage
 * already reads a date without an owner - it falls back to the current user - so what
 * was missing was only the record to match on.
 */
const EVENT_DAY_SCOPE_PATH = ':date(\\d{4}-\\d{2}-\\d{2})/';
const EVENT_CALENDAR_SCOPE_PATH = EVENT_DAY_SCOPE_PATH + ':calendarOwnerId(\\d+)/:calendarOwnerName/';

// what the scoped variants add to their mount's own name prefix, so the records'
// children do not collide. Nothing navigates to these names literally: the
// components go through eventRouteName(), which reads the prefix off the record that
// actually matched, so a page keeps whichever shape it was reached by
const EVENT_DAY_SCOPE_NAME_PREFIX = 'dayScoped-';
const EVENT_CALENDAR_SCOPE_NAME_PREFIX = 'calendarScoped-';

/**
 * The three route records one event mount needs - plain, scoped to a calendar day, and
 * scoped to a calendar day and owner. `buildRecord({pathTail, namePrefix, nameSuffix})`
 * is called once per variant and returns that variant's record.
 */
const getEventDetailPageRecords = (namePrefix = '', buildRecord) => [
    buildRecord({pathTail: '', namePrefix, nameSuffix: ''}),
    buildRecord({
        pathTail: EVENT_DAY_SCOPE_PATH,
        namePrefix: namePrefix + EVENT_DAY_SCOPE_NAME_PREFIX,
        nameSuffix: '-day-scoped',
    }),
    buildRecord({
        pathTail: EVENT_CALENDAR_SCOPE_PATH,
        namePrefix: namePrefix + EVENT_CALENDAR_SCOPE_NAME_PREFIX,
        nameSuffix: '-calendar-scoped',
    }),
];

const getEventDetailPageChildren = (namePrefix = '') => [
    {
        name: namePrefix + 'info',
        path: 'info',
        component: () => import(/* webpackChunkName: "eventDetail" */ './EventDetailPageInfo.vue'),
        props: true,
        meta: {name: 'info'}
    },
    {
        name: namePrefix + 'members',
        path: 'members',
        component: () => import(/* webpackChunkName: "eventDetail" */ './EventDetailPageMembers.vue'),
        props: true
    },
    {
        name: namePrefix + 'goals',
        path: 'goals',
        component: () => import(/* webpackChunkName: "eventDetail" */ './EventDetailPageGoals.vue'),
        props: true
    },
    {
        name: namePrefix + 'comments',
        path: 'comments',
        component: () => import(/* webpackChunkName: "eventDetail" */ './EventDetailPageComments.vue'),
        props: true
    },
    {
        name: namePrefix + 'event_no_access_page',
        path: 'access-restricted',
        component: () => import(/* webpackChunkName: "eventDetail" */ './EventDetailErrorNoAccess.vue'),
        props: true
    },
    {
        name: namePrefix + 'event_does_not_occur',
        path: 'empty',
        component: () => import(/* webpackChunkName: "eventDetail" */ './EventDetailErrorDeleted.vue'),
        props: true
    },
    // by name, not by the relative path 'info': router 4 resolves a string redirect
    // against the root rather than against the parent record, so 'info' would leave the
    // event pages entirely. The name carries the mount's prefix with it.
    //
    // The redirect record is named too: router 4 warns about an unnamed empty-path child,
    // because navigating to its parent by name renders the parent around an empty
    // router-view instead of following this. Nothing navigates to this name - it is the
    // handle that makes the redirect reachable by one, the way the wiki children are named
    {
        name: namePrefix + 'event-index',
        path: '',
        redirect: to => ({name: namePrefix + 'info', params: to.params}),
        props: true
    },
];

/**
 * Mixin for the components that navigate between those pages by name: they can be
 * rendered under either mount, so they ask their own route which one it is.
 */
const eventRouteNames = {
    methods: {
        eventRouteName (name) {
            const record = this.$route.matched.find(
                r => r.meta && typeof r.meta[EVENT_NAME_PREFIX_META] === 'string'
            );
            return (record ? record.meta[EVENT_NAME_PREFIX_META] : '') + name;
        }
    }
};

export default getEventDetailPageChildren;
export {
    getEventDetailPageChildren,
    getEventDetailPageRecords,
    eventRouteNames,
    EVENT_NAME_PREFIX_META,
    EVENT_DAY_SCOPE_PATH,
    EVENT_CALENDAR_SCOPE_PATH,
};
